import type { ReactElement } from 'react';
import React, { useEffect } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { endOfYear } from 'date-fns';
import { useFormikContext } from 'formik';
import DateRange from 'atom-components/form-fields-for-formik/date-range-field/date-range-field';
import { SelectField } from 'atom-components/form-fields-for-formik/select-field/select-field';
import { formatDateForAPI } from 'helpers/date.helpers';
import type { ClusterFieldValue, PeriodFieldValue } from 'pages/clusters/types';
import { AreaType } from 'services/assist-api/entities/area/areas';
import type { BasePeriod } from 'services/assist-api/entities/period/periods';
import { usePeriods } from 'services/assist-api/entities/period/periods';
import { useClusterFilterYears } from 'services/assist-api/entities/test-instance-response/filters/customer-group/year';
import type { Year } from 'services/assist-api/entities/test-instance-response/filters/customer-group/year';
import { isInitialLoad } from 'utils/check-query-status';

const CURRENT_DATE = formatDateForAPI(new Date());

/**
 * Date range selection
 * - displays calendar selection
 * - intended for non-schools as they don't abide by periods/terms
 */
function SelectDateRange(): ReactElement {
  return (
    <DateRange
      startProps={{
        name: 'period.startDate',
        label: 'Start Date',
        placeholderText: 'Start Date',
        formGroupClassName: 'w-100',
        hideSuccessValidation: true
      }}
      endProps={{
        name: 'period.endDate',
        label: 'End Date',
        placeholderText: 'End Date',
        formGroupClassName: 'w-100',
        hideSuccessValidation: true
      }}
      maxDate={endOfYear(new Date())}
    />
  );
}

/**
 * Fields to help the school user select a date range
 * - first prompts for calendar year
 * - then lists periods/terms in that year
 */
function SelectYearAndPeriod(): ReactElement {
  /* Get existing form state */
  const {
    values: { year, cluster },
    setFieldValue
  } = useFormikContext<PeriodFieldValue & ClusterFieldValue>();

  /* Request years with data from the API */
  const years: UseQueryResult<RequestResponse<Year[]>> = useClusterFilterYears(
    {
      filters: {
        'testInstance.area.type': AreaType.Assessment,
        'testInstance.customer.customerGroupsCustomers.customerGroup': cluster
      }
    },
    { enabled: !!cluster }
  );

  /* Request periods in the given year from the API */
  const periods = usePeriods(
    { filters: { year, 'startDate[before]': CURRENT_DATE } },
    { enabled: !!year }
  );

  /* Reset fields if cluster changes */
  useEffect(
    () => (): void => {
      setFieldValue('year', '');
      setFieldValue('period', '');
    },
    [setFieldValue, cluster]
  );

  /* Reset period field if year changes */
  useEffect(
    () => (): void => {
      setFieldValue('period', '');
    },
    [setFieldValue, year]
  );

  return (
    <>
      <SelectField
        name="year"
        label="Year"
        options={years.data?.json || []}
        isLoading={isInitialLoad(years)}
        getOptionValue={(o): string => String(o.value)}
        getOptionLabel={(o): string => o.name}
        isDisabled={!cluster}
        hideSuccessValidation
        isRequired
        isOnlyValue
      />
      <SelectField
        name="period"
        label="Period"
        options={periods.data?.json || []}
        isLoading={isInitialLoad(periods)}
        getOptionLabel={(o: BasePeriod): string => o.name}
        getOptionValue={(o: BasePeriod): string => String(o.id)}
        isDisabled={!year}
        hideSuccessValidation
        isRequired
      />
    </>
  );
}

export const initialDateRange = { year: undefined, period: { startDate: '', endDate: '' } };

export default function DateRangeField({ isSchool }: { isSchool: boolean }): ReactElement {
  return isSchool ? <SelectYearAndPeriod /> : <SelectDateRange />;
}
